import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { invalidateAllCaches } from '@/api/cacheInvalidation';

/**
 * Response from GET /api/account/purge/entities
 * Same structure as admin's UserEntitiesResponse
 */
export interface OwnEntitiesResponse {
    collectionsCount: number;
    tagsCount: number;
    ledgersCount: number;
    collections: Array<{ id: number; label: string; type: string }>;
    tags: Array<{ id: number; label: string; type: string }>;
    ledgers: Array<{
        id: number;
        label: string;
        type: string;
        valuesCount: number;
        returnsCount: number;
    }>;
}

/**
 * Response from POST /api/account/purge
 */
export interface PurgeOwnAccountResponse {
    success: boolean;
    deletedCounts: Record<string, number>;
    totalDeleted: number;
    message: string;
    creditsRefunded: number;
    refundBreakdown: Record<string, number>;
}

/**
 * Get current user's entity counts for purge preview.
 */
export function useOwnEntities() {
    return useQuery<OwnEntitiesResponse>({
        queryKey: ['account', 'purge', 'entities'],
        queryFn: () => api.get('/api/account/purge/entities'),
    });
}

/**
 * Purge all data for the current user.
 * Requires confirmation phrase.
 */
export function usePurgeOwnAccount() {
    const queryClient = useQueryClient();

    return useMutation<PurgeOwnAccountResponse, Error, string>({
        mutationFn: (confirmationPhrase) =>
            api.post('/api/account/purge', { confirmationPhrase }),
        onSuccess: () => {
            // Clear all caches after purge - all data is gone
            invalidateAllCaches(queryClient);
        },
    });
}
